import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Button,
  Avatar,
  Chip,
} from "@mui/material";
import {
  Business,
  Save,
  School,
  Add,
  FileCopyOutlined,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import OverlayLoader from "../../../../components/OverlayLoader";
import HttpClient from "../../../../helpers/HttpClient";
import { showToast } from "../../../../utils/toast";
import CustomAutocomplete from "../../Components/CustomAutocomplete";
import CourseFileManagerDialog from "../../Components/CourseFileManagerDialog";

export default function DistributorConstancePage() {
  const [openLoader, setOpenLoader] = useState(false);
  const [openCourseDialog, setOpenCourseDialog] = useState(false);
  const [distribuidorSeleccionado, setDistribuidorSeleccionado] = useState(null);
  const [distribuidoresDisponibles, setDistribuidoresDisponibles] = useState([]);
  const [cursosDisponibles, setCursosDisponibles] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);

  const authState = useSelector((state) => state.auth);
  const userId = authState.user ? authState.user.email : "test";

  /** Carga de cursos */
  const getCourses = useCallback(async () => {
    setOpenLoader(true);
    try {
      const result = await HttpClient().get(`/admin/courses`);
      if (result.error) {
        showToast("Error cargando cursos.", "error");
        return;
      }
      setCursosDisponibles(result.data);
    } catch {
      showToast("Error al cargar cursos.", "error");
    } finally {
      setOpenLoader(false);
    }
  }, []);

  /** Carga de distribuidores */
  const getDistributors = useCallback(async () => {
    setOpenLoader(true);
    try {
      const result = await HttpClient().get(`/admin/distributor`);
      if (result.error) {
        showToast("Error cargando distribuidores.", "error");
        return;
      }
      setDistribuidoresDisponibles(result.data);
    } catch {
      showToast("Error al cargar distribuidores.", "error");
    } finally {
      setOpenLoader(false);
    }
  }, []);

  useEffect(() => {
    getCourses();
    getDistributors();
  }, [getCourses, getDistributors]);

  /** Vista previa de archivo */
  const handlePreviewFile = (file) => {
    if (!file) return alert("No hay archivo para mostrar.");
    const fileURL =
      file instanceof File
        ? URL.createObjectURL(file)
        : typeof file === "string" && file.startsWith("http")
        ? file
        : null;

    if (fileURL) window.open(fileURL, "_blank", "noopener,noreferrer");
    else alert("Archivo no válido o no disponible.");
  };

  /** Guardar distribuidor + cursos */
  const handleGuardar = async () => {
    if (!distribuidorSeleccionado) {
      showToast("Selecciona un distribuidor.", "warning");
      return;
    }

    const validCourses = selectedCourses.filter(
      (c) => c.constancia && c.file
    );
    if (validCourses.length === 0) {
      showToast("Debes agregar al menos un curso válido.", "warning");
      return;
    }

    const formData = new FormData();
    formData.append("distributor_id", distribuidorSeleccionado.id);
    formData.append("user", userId);

    validCourses.forEach((curso, idx) => {
      formData.append(`courses[${idx}][code]`, curso.code);
      formData.append(`courses[${idx}][constancia]`, curso.constancia);
      formData.append(`courses[${idx}][file_name]`, curso.file.name);
      formData.append(`courses[${idx}][file]`, curso.file);
    });

    setOpenLoader(true);
    const result = await HttpClient().post(`/admin/distributor/certificates`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    setOpenLoader(false);

    if (result.error) {
      showToast("Error al guardar constancias.", "error");
      return;
    }

    if (result.status === 201) {
      showToast("Constancias enviadas correctamente.", "success");
      setSelectedCourses([]);
      setDistribuidorSeleccionado(null);
    } else {
      showToast(
        "No se pudo crear la constancia. Intenta nuevamente.",
        "warning"
      );
    }

    
    
  };

  return (
    <Box sx={{ backgroundColor: "#F5F5F5", minHeight: "100vh", p: 2 }}>
      <OverlayLoader open={openLoader} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="#374151">
          Subir constancias por distribuidor/empresa
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, border: "1px solid #E5E7EB", backgroundColor: "white" }}>
        <CardContent sx={{ p: 2 }}>
          {/* Distribuidor */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Business sx={{ color: "#6B7280", fontSize: 16 }} />
                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                  Distribuidor:
                </Typography>
              </Box>
              <CustomAutocomplete
                options={distribuidoresDisponibles}
                value={distribuidorSeleccionado}
                onChange={(event, newValue) => setDistribuidorSeleccionado(newValue)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Cursos seleccionados */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <School sx={{ color: "#6B7280", fontSize: 16 }} />
              <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.85rem" }}>
                Curso(s) seleccionados: ({selectedCourses.length})
              </Typography>
            </Box>

            <Box
              sx={{
                border: "1px solid #E5E7EB",
                borderRadius: 1.5,
                p: 1,
                backgroundColor: "#F9FAFB",
                minHeight: 80,
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {selectedCourses.length === 0 ? (
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 80,
                    color: "#9CA3AF",
                  }}
                >
                  No hay cursos seleccionados
                </Typography>
              ) : (
                selectedCourses.map((curso) => (
                  <Box
                    key={curso.code}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                      p: 1,
                      backgroundColor: "white",
                      borderRadius: 1.5,
                      border: "1px solid #E5E7EB",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        label={curso.code}
                        size="small"
                        sx={{
                          backgroundColor: "#10B981",
                          color: "white",
                          fontWeight: "bold",
                          mr: 0.8,
                          fontSize: "0.7rem",
                          height: 20,
                        }}
                      />
                      <Typography variant="caption">{curso.name}</Typography>
                    </Box>
                    {curso.file && (
                      <Typography
                        onClick={() => handlePreviewFile(curso.file)}
                        variant="body2"
                        fontWeight="bold"
                        color="#0369A1"
                        sx={{
                          fontSize: "0.8rem",
                          cursor: "pointer",
                          textDecoration: "underline",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <FileCopyOutlined sx={{ fontSize: 16 }} /> {curso.constancia}
                      </Typography>
                    )}
                  </Box>
                ))
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<Add sx={{ fontSize: 14 }} />}
                disabled={selectedCourses.length > 0}
                onClick={() => setOpenCourseDialog(true)}
                sx={{
                  backgroundColor: "#10B981",
                  "&:hover": { backgroundColor: "#059669" },
                  borderRadius: 1.5,
                  fontSize: "0.75rem",
                }}
              >
                Gestionar cursos
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Guardar */}
          <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleGuardar}
              disabled={!distribuidorSeleccionado || selectedCourses.length === 0}
              sx={{
                backgroundColor: "#10B981",
                color: "white",
                fontWeight: "bold",
                px: 3,
                py: 1,
                borderRadius: 2,
                fontSize: "0.8rem",
                "&:hover": { backgroundColor: "#059669" },
                "&:disabled": {
                  backgroundColor: "#D1D5DB",
                  color: "#9CA3AF",
                },
              }}
            >
              Guardar constancias
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Dialog de cursos */}
      <CourseFileManagerDialog
        open={openCourseDialog}
        onClose={() => {
          setSelectedCourses((prev) => prev.filter((c) => c.constancia && c.file));
          setOpenCourseDialog(false)
        }}
        availableCourses={
          cursosDisponibles?.filter(
            (curso) => !selectedCourses.find((c) => c.code === curso.code)
          ) || []
        }
        selectedCourses={selectedCourses}
        onAddCourse={(curso) => {
          if (selectedCourses.length > 0 ) {
            alert("Solo puedes subir un fichero a la vez.") 
            return
          }
          if (!selectedCourses.some((c) => c.code === curso.code)) {
            setSelectedCourses((prev) => [...prev, { ...curso, constancia: "", file: null }]);
          }
        }}
        onRemoveCourse={(code) => {
          setSelectedCourses((prev) => prev.filter((c) => c.code !== code));
        }}
        onUpdateConstancia={(code, value) => {
          setSelectedCourses((prev) =>
            prev.map((c) => (c.code === code ? { ...c, constancia: value } : c))
          );
        }}
        onUploadFile={(code, file) => {
          setSelectedCourses((prev) =>
            prev.map((c) => (c.code === code ? { ...c, file } : c))
          );
        }}
        onSave={() => setOpenCourseDialog(false)}
      />
    </Box>
  );
}
